import { useState, useEffect, useCallback } from "react";
import { FormLayout, TextField, Select, Button, Banner, BlockStack } from "@shopify/polaris";
import type { CampaignVariant } from "../../api/campaigns";
import { isShopInstallRedirectError } from "../../api/admin-api-client";
import { SCRATCH_VARIANT_HELP } from "../games/scratchProductCopy";

const THEME_OPTIONS = [
  { label: "Foil (default)", value: "foil" },
  { label: "Silver", value: "silver" },
  { label: "Gold", value: "gold" },
];

type ScratchTheme = "foil" | "silver" | "gold";

function parseScratchConfig(raw: unknown): { ctaText: string; theme: ScratchTheme } {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const ctaText =
    typeof o.ctaText === "string" && o.ctaText.trim().length > 0
      ? o.ctaText.trim()
      : "Scratch to reveal";
  const t = o.theme;
  const theme: ScratchTheme = t === "silver" || t === "gold" ? t : "foil";
  return { ctaText, theme };
}

export interface ScratchVariantEditorProps {
  variant: CampaignVariant;
  onUpdateVariant: (
    variantId: string,
    updates: { config?: Record<string, unknown> }
  ) => Promise<void>;
  onSaved?: () => Promise<void>;
}

export function ScratchVariantEditor({
  variant,
  onUpdateVariant,
  onSaved,
}: ScratchVariantEditorProps) {
  const [ctaText, setCtaText] = useState("");
  const [theme, setTheme] = useState<ScratchTheme>("foil");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const parsed = parseScratchConfig(variant.config);
    setCtaText(parsed.ctaText);
    setTheme(parsed.theme);
  }, [variant.id, variant.config]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    const prev =
      variant.config && typeof variant.config === "object"
        ? (variant.config as Record<string, unknown>)
        : {};
    try {
      await onUpdateVariant(variant.id, {
        config: {
          ...prev,
          ctaText,
          theme,
        },
      });
      await onSaved?.();
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to save scratch settings");
    } finally {
      setSaving(false);
    }
  }, [ctaText, theme, variant.config, variant.id, onUpdateVariant, onSaved]);

  return (
    <BlockStack gap="400">
      <Banner tone="info">
        <p style={{ margin: 0 }}>{SCRATCH_VARIANT_HELP}</p>
      </Banner>
      {error && (
        <Banner tone="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}
      <FormLayout>
        <TextField
          label="Call-to-action text"
          value={ctaText}
          onChange={setCtaText}
          helpText="Shown on the scratch surface before reveal (storefront)."
          autoComplete="off"
        />
        <Select
          label="Surface finish"
          options={THEME_OPTIONS}
          value={theme}
          onChange={(v) => setTheme(v as ScratchTheme)}
          helpText="Visual theme for the card on your storefront (foil, silver, or gold)."
        />
      </FormLayout>
      <div style={{ marginTop: "0.25rem" }}>
        <Button variant="primary" onClick={() => void handleSave()} loading={saving}>
          Save scratch settings
        </Button>
      </div>
    </BlockStack>
  );
}
